import { Profile, profileMinHeight } from '@/src/common/components/Profile'
import Animated, {
  WithTimingConfig,
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated'
import { create } from 'zustand'
import { useWindowDimensions } from 'react-native'
import {
  type MainScreen,
  maxWidth,
  useMainWrapperContentOffset,
  useMainWrapperPadding,
} from '@/src/common/wrappers/MainWrapper'
import { useMediaQuery } from 'react-responsive'
import { type PropsWithChildren, useEffect } from 'react'
import { usePathname } from 'expo-router'
import { MainWrapperProfileTestID } from '@/src/common/wrappers/MainWrapper/testIDs'
import { mesaColorNames } from '@/src/common/theme'

export function useIsTabBarPositionedTop() {
  return useMediaQuery({ minWidth: 1024 })
}

/** @todo calculate this dynamically? */
export const topTabBarHeight = 121

const topBarCollapseScrollThreshold = (2 / 3) * topTabBarHeight

export const useTabBarState = create<{
  tabBarExpanded: boolean
  setTabBarExpanded: (tabBarExpanded: boolean) => void
}>((set) => ({
  tabBarExpanded: true,
  setTabBarExpanded: (tabBarExpanded) => set({ tabBarExpanded }),
}))

const timingConfig = {
  duration: 222,
  easing: Easing.inOut(Easing.quad),
  reduceMotion: ReduceMotion.System,
} as const satisfies WithTimingConfig

export function TopTabBar({ children }: PropsWithChildren) {
  const { width: windowWidth } = useWindowDimensions()
  const mainWrapperPadding = useMainWrapperPadding()
  const { tabBarExpanded, setTabBarExpanded } = useTabBarState()
  const pathname = usePathname()
  const { contentOffsets } = useMainWrapperContentOffset()
  const contentOffsetY = contentOffsets[pathname]?.y || 0

  useEffect(
    function handleExpansionForScreen() {
      switch (pathname) {
        case '/main/menu':
        case '/main/pii-menu':
        case '/main/notification-center':
        case '/main/notification-settings':
          pathname satisfies MainScreen
          break
        case '/main/home':
        case '/main/rewards':
        case '/main/rewards/points-transfer':
        case '/main/card/cardBenefits':
        case '/main/home/earn-on-mortgage':
        case '/main/home/earn-on-mortgage-document-upload':
          pathname satisfies MainScreen
          setTabBarExpanded(contentOffsetY < topBarCollapseScrollThreshold)
          break
        default:
          setTabBarExpanded(true)
      }
    },
    [pathname, setTabBarExpanded, contentOffsetY],
  )

  const tabBarStyles = useAnimatedStyle(
    () =>
      ({
        position: 'absolute',
        alignSelf: 'center',
        padding: withTiming(tabBarExpanded ? mainWrapperPadding : 0, timingConfig),
        paddingTop: withTiming(tabBarExpanded ? mainWrapperPadding : 0, {
          ...timingConfig,
          duration: 88,
        }),
        maxWidth: withTiming(tabBarExpanded ? maxWidth : windowWidth, timingConfig),
        margin: 'auto',
        width: '100%',
        paddingBottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        zIndex: 8,
        marginBottom: -topTabBarHeight,
        minHeight: withTiming(tabBarExpanded ? profileMinHeight : 0, timingConfig),
      }) as const,
  )

  const profileStyles = useAnimatedStyle(() => {
    const paddingVertical = tabBarExpanded ? 16 : 8
    const paddingHorizontal = tabBarExpanded ? 16 : mainWrapperPadding
    return {
      borderRadius: withTiming(tabBarExpanded ? 16 : 0, {
        ...timingConfig,
        easing: tabBarExpanded ? Easing.linear : Easing.exp,
      }),
      paddingTop: withTiming(paddingVertical, timingConfig),
      paddingBottom: withTiming(paddingVertical, timingConfig),
      paddingLeft: withTiming(paddingHorizontal, timingConfig),
      paddingRight: withTiming(paddingHorizontal, timingConfig),
    } as const
  })

  return (
    <Animated.View style={tabBarStyles}>
      <Profile
        disabled
        style={[{ background: mesaColorNames['Mesa - Brand/White #FFFFFF'] }, profileStyles]}
        contentWrapperStyle={{ justifyContent: 'center', minHeight: 0 }}
        menuWrapperProps={{ style: { position: 'absolute', left: 0 } }}
        noticationWrapperProps={{ style: { position: 'absolute', right: 0 } }}
        testID={MainWrapperProfileTestID}
      >
        {children}
      </Profile>
    </Animated.View>
  )
}
